/* eslint-disable react/no-array-index-key */
import { Button, Paper, Stack } from '@mui/material';
import { DataGrid, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import React from 'react';
import { useFormContext, UseFormReturn } from 'react-hook-form';

import { remove } from '../../../api/ventura/locationList';
import useAdGroupLocationLists from '../../../hooks/useAdGroupLocationLists';
import { useConfig } from '../../../hooks/useConfig';
import useModal from '../../../hooks/useModal';
import { Link } from '../../../inmarkit';
import { AdGroup } from '../../../validationSchemas/AdGroupSchema';
import CollapsiblePaper from '../../shared/CollapsiblePaper';
import LocationListForm from './LocationListForm';

function LocationListsTable() {
  const { poiManagementApiHost } = useConfig();
  const { getValues }: UseFormReturn<AdGroup> = useFormContext();

  const [adGroupId, adGroupName, locationsCount] = getValues(['id', 'name', 'locationsCount']);
  const { data, refetch, isLoading } = useAdGroupLocationLists(adGroupId);
  const { apiHost } = useConfig();
  const [selected, setSelected] = React.useState<GridRowSelectionModel>([]);

  const deleteLocationList = useMutation({
    mutationFn: (locationListId: number) => remove(apiHost, locationListId),
    onSuccess: (response) => {
      if (!response.exception) {
        refetch();
      }
    },
  });

  const deleteSelected = () => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Are you sure you want to delete the selected location lists?')) {
      selected.forEach((locationListId) => {
        deleteLocationList.mutate(Number(locationListId));
      });
    }
  };

  // eslint-disable-next-line react/jsx-props-no-spreading
  const [showAddLocationListForm, closeAddLocationListForm] = useModal(
    () => (
      <LocationListForm
        locationList={null}
        adGroupId={adGroupId}
        adGroupName={adGroupName}
        onClose={closeAddLocationListForm}
      />
    ),
    null,
    [adGroupId, adGroupName]
  );

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      renderCell: (params: any) => {
        const locationList = params.row;
        return (
          <Link
            href={`${poiManagementApiHost}/poiManagement/location-list/${locationList.location_list_id}`}
            target="_blank"
            color="#616161"
          >
            {locationList.location_list_id}
          </Link>
        );
      },
      flex: 1,
    },
    {
      field: 'location_list_count',
      headerName: 'Locations Count',
      flex: 1,
    },
  ];

  return (
    <CollapsiblePaper title={`Custom Location Lists (${data?.collection?.length > 0 ? locationsCount : 0})`}>
      <Paper sx={{ px: 2, py: 3 }}>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Link disabled href="/">
              View All Location Lists
            </Link>
            <Stack spacing={1} direction="row" justifyContent="end">
              {!isEmpty(data?.collection) && !isEmpty(selected) && (
                <Button onClick={() => deleteSelected()} variant="outlined" color="error">
                  Delete
                </Button>
              )}
              <Button variant="outlined" onClick={showAddLocationListForm}>
                Add Custom Location List
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.open('https://360platform.inmarket.com/poiManagement/location-list', '_blank')}
              >
                Create New Location List
              </Button>
            </Stack>
          </Stack>
          <DataGrid
            columns={columns}
            rows={data?.collection || []}
            rowCount={data?.collection.length}
            checkboxSelection
            disableRowSelectionOnClick
            loading={isLoading}
            rowSelectionModel={selected}
            onRowSelectionModelChange={setSelected}
          />
        </Stack>
      </Paper>
    </CollapsiblePaper>
  );
}

export default LocationListsTable;
